import React from 'react';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { getProvider } from '../registry';

// Derive a short, analytics-friendly label for the click target so we can
// segment which card/search drove the click without dumping raw objects.
function targetLabel(target) {
    if (!target) return null;
    switch (target.kind) {
        case 'card':
            return target.card?.name || target.card?.id || null;
        case 'search':
            return target.query || null;
        case 'wrap':
            return target.url || null;
        case 'cart':
            return target.deck?.id || 'deck';
        default:
            return null;
    }
}

// Fire-and-forget analytics. Never let a telemetry failure break the
// navigation — the click still opens the affiliate link regardless.
function logBuyClick(providerId, placement, target) {
    try {
        const eventName = providerId === 'tcgplayer' ? 'buy_tcg_click' : `buy_${providerId}_click`;
        logEvent(getAnalytics(), eventName, {
            provider: providerId,
            placement: placement || 'unknown',
            target_kind: target?.kind || 'unknown',
            target_label: targetLabel(target) || undefined,
        });
    } catch (_err) {
        // analytics not initialized / blocked — ignore
    }
}

// Single render surface for every affiliate link on the site. Owns
// rel="sponsored noopener noreferrer" + target="_blank" + analytics
// hooks so per-button code can't forget them, and returns null when the
// provider can't build a URL for the target (e.g. vendor doesn't carry
// the card). Add new placements by calling <AffiliateLink/> — never by
// hand-rolling an <a href="…partner.tcgplayer.com…">.
//
// Props:
//   provider:  provider id ('tcgplayer', …)
//   target:    { kind: 'card', card }
//            | { kind: 'cart', deck }
//            | { kind: 'search', query }
//            | { kind: 'wrap',   url   }
//   placement: short label for analytics segmentation
//              (e.g. 'printslist', 'upgradelizer', 'deckcart')
//   sharedId:  optional Impact "sharedid" param
//   className: extra class on the rendered <a>
//   onClick:   optional click hook for analytics — fires alongside navigation
export function AffiliateLink({
    provider: providerId,
    target,
    placement,
    sharedId,
    className,
    children,
    onClick,
    title,
    'aria-label': ariaLabel,
}) {
    const provider = getProvider(providerId);
    if (!provider || !target) return null;
    const ctx = { placement, sharedId };
    let href = null;
    switch (target.kind) {
        case 'card':
            href = provider.cardUrl(target.card, ctx);
            break;
        case 'cart':
            href = provider.cartUrl(target.deck, ctx);
            break;
        case 'search':
            href = provider.searchUrl(target.query, ctx);
            break;
        case 'wrap':
            href = provider.wrap(target.url, ctx);
            break;
        default:
            href = null;
    }
    if (!href) return null;
    return (
        <a
            className={className}
            href={href}
            target="_blank"
            rel="sponsored noopener noreferrer"
            title={title}
            aria-label={ariaLabel}
            onClick={(e) => {
                logBuyClick(provider.id, placement, target);
                if (onClick) onClick(e, { provider: provider.id, placement });
            }}
        >
            {children}
        </a>
    );
}
